import { Pagination } from "services/search";

import {
  Contact,
  ContactAddress,
  ContactAddressEditFormValues,
  ContactAddressRow,
  ContactEditFormValues,
  ContactEmail,
  ContactEmailEditFormValues,
  ContactEmailRow,
  ContactFile,
  ContactGroupRow,
  ContactImportXlsFormValues,
  ContactImportXlsResult,
  ContactNote,
  ContactNoteEditFormValues,
  ContactNoteRow,
  ContactPhone,
  ContactPhoneEditFormValues,
  ContactPhoneRow,
  ContactRelationEditFormValues,
  ContactRelationRow,
  ContactRow,
  SudRegResponse,
} from "./types";

import { DataWithPagination } from "services/api/types";
import {
  transformContact,
  transformContactAddress,
  transformContactAddressOnCreate,
  transformContactAddressOnUpdate,
  transformContactAddressRow,
  transformContactEmail,
  transformContactEmailOnCreate,
  transformContactEmailOnUpdate,
  transformContactEmailRow,
  transformContactGroupRow,
  transformContactImport,
  transformContactNote,
  transformContactNoteOnCreate,
  transformContactNoteOnUpdate,
  transformContactNoteRow,
  transformContactOnCreate,
  transformContactOnUpdate,
  transformContactPhone,
  transformContactPhoneOnCreate,
  transformContactPhoneOnUpdate,
  transformContactPhoneRow,
  transformContactRow,
} from "./transformations";
import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { getAccessToken } from "services/auth";
import { filesApi } from "../config";
import { AxiosResponse } from "axios";
import { transformErrorResponse } from "../transformation";
import { FleetDocumentUpload } from "../fleet/vehicles/documents/types";
import { DropdownCacheKey, invalidateDropdownCache } from "services/hooks/useDropdownCache";


/*---------------------------------------------------------------------------------------
  CONTACT SEARCH
-----------------------------------------------------------------------------------------*/
export const getContactsAPI = async (
  pagination: Pagination,
  name?: string | null,
  contactTypeId?: string | null,
  contactGroupIds?: string[] | null,
  email?: string | null,
  phone?: string | null,
  address?: string | null
): Promise<DataWithPagination<ContactRow>> => {
  let path = `/v1/contacts?name=`;

  if (name) path += `${name}`;
  if (contactTypeId) path += `&contactTypeId=${contactTypeId}`;
  if (contactGroupIds && contactGroupIds.length > 0) path += `&contactGroupIds=${contactGroupIds.join(",")}`;
  if (email) path += `&email=${email}`;
  if (phone) path += `&phone=${phone}`;
  if (address) path += `&address=${address}`;

  return await paginatedApiCall(path, pagination, transformContactRow);
};

/*---------------------------------------------------------------------------------------
    CONTACT - BY ID
-----------------------------------------------------------------------------------------*/
export const getContactAPI = async (id: string): Promise<Contact> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContact(response.data);
};

/*---------------------------------------------------------------------------------------
    CONTACT - CREATE
-----------------------------------------------------------------------------------------*/
export const createContactAPI = async (
  formValues: ContactEditFormValues,
  uploads: FleetDocumentUpload [],
): Promise<Contact> => {
  const response = await createApiCall(
    {
      path: "/v1/contacts/",
      method: "POST",
      body: transformContactOnCreate(formValues, uploads),
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.Contacts);
  return transformContact(response.data);
};

/*---------------------------------------------------------------------------------------
    CONTACT - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateContactAPI = async (
  id: string,
  formValues: ContactEditFormValues,
  uploads: FleetDocumentUpload [],
): Promise<Contact> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${id}`,
      method: "PUT",
      body: transformContactOnUpdate(formValues, uploads),
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.Contacts);
  return transformContact(response.data);
};

/*---------------------------------------------------------------------------------------
    CONTACT - DELETE
-----------------------------------------------------------------------------------------*/
export const deleteContactAPI = async (
  t: (key: string) => string,
  id: string): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.contact.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.Contacts);
  return response.data?.deleted;
};



/*---------------------------------------------------------------------------------------
    CONTACT - PUBLISH
-----------------------------------------------------------------------------------------*/
export const publishContactAPI = async (
  id: string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${id}/publish`,
      method: "PUT",
    },
    {
      getMessage: {
        success: () => "Kontakt je uspješno objavljen",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.published;
};

/*---------------------------------------------------------------------------------------
    EMAILS - SEARCH
-----------------------------------------------------------------------------------------*/

export const getContactEmailsAPI = async (
  pagination: Pagination,
  contactId: string,
  name?: string | null
): Promise<DataWithPagination<ContactEmailRow>> => {
  let path = `/v1/contacts/${contactId}/emails?name=`;

  if (name) path += `${name}`;
  return await paginatedApiCall(path, pagination, transformContactEmailRow);
};

/*---------------------------------------------------------------------------------------
    EMAILS - BY ID
-----------------------------------------------------------------------------------------*/
export const getContactEmailAPI = async (
  contactId: string,
  emailId: string
): Promise<ContactEmail> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/emails/${emailId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactEmail(response.data);
};

/*---------------------------------------------------------------------------------------
    EMAILS - CREATE
-----------------------------------------------------------------------------------------*/
export const createContactEmailAPI = async (
  contactId: string,
  contactEmailEditFormValues: ContactEmailEditFormValues
): Promise<ContactEmail> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/emails/`,
      method: "POST",
      body: transformContactEmailOnCreate(contactEmailEditFormValues),
    },
    {
      getMessage: {
        success: () => "Email je uspješno kreiran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactEmail(response.data);
};

/*---------------------------------------------------------------------------------------
    EMAILS - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateContactEmailAPI = async (
  contactId: string,
  emailId: string,
  contactEmailEditFormValues: ContactEmailEditFormValues
): Promise<ContactEmail> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/emails/${emailId}`,
      method: "PUT",
      body: transformContactEmailOnUpdate(contactEmailEditFormValues),
    },
    {
      getMessage: {
        success: () => "Email je uspješno ažuriran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactEmail(response.data);
};

/*---------------------------------------------------------------------------------------
    CONTACT - EMAIL - DELETE
-----------------------------------------------------------------------------------------*/
export const deleteContactEmailAPI = async (
  t: (key: string) => string,
  contactId: string,
  emailId: string,
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/emails/${emailId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.contact.email.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};


/*---------------------------------------------------------------------------------------
    PHONE - SEARCH
-----------------------------------------------------------------------------------------*/

export const getContactPhonesAPI = async (
  pagination: Pagination,
  contactId: string,
  name?: string | null
): Promise<DataWithPagination<ContactPhoneRow>> => {
  let path = `/v1/contacts/${contactId}/phones?name=`;

  if (name) path += `${name}`;
  return await paginatedApiCall(path, pagination, transformContactPhoneRow);
};

/*---------------------------------------------------------------------------------------
      PHONE - BY ID
  -----------------------------------------------------------------------------------------*/
export const getContactPhoneAPI = async (
  contactId: string,
  phoneId: string
): Promise<ContactPhone> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/phones/${phoneId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactPhone(response.data);
};

/*---------------------------------------------------------------------------------------
      PHONE - CREATE
  -----------------------------------------------------------------------------------------*/
export const createContactPhoneAPI = async (
  contactId: string,
  contactPhoneEditFormValues: ContactPhoneEditFormValues
): Promise<ContactPhone> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/phones/`,
      method: "POST",
      body: transformContactPhoneOnCreate(contactPhoneEditFormValues),
    },
    {
      getMessage: {
        success: () => "Telefon je uspješno kreiran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactPhone(response.data);
};

/*---------------------------------------------------------------------------------------
      PHONE - UPDATE
  -----------------------------------------------------------------------------------------*/
export const updateContactPhoneAPI = async (
  contactId: string,
  phoneId: string,
  contactPhoneEditFormValues: ContactPhoneEditFormValues
): Promise<ContactPhone> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/phones/${phoneId}`,
      method: "PUT",
      body: transformContactPhoneOnUpdate(contactPhoneEditFormValues),
    },
    {
      getMessage: {
        success: () => "Telefon je uspješno ažuriran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactPhone(response.data);
};

/*---------------------------------------------------------------------------------------
    CONTACT - PHONE - DELETE
-----------------------------------------------------------------------------------------*/
export const deleteContactPhoneAPI = async (
  t: (key: string) => string,
  contactId: string,
  phoneId: string,
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/phones/${phoneId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.contact.phone.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};


/*---------------------------------------------------------------------------------------
    ADRESA - SEARCH
-----------------------------------------------------------------------------------------*/

export const getContactAddressesAPI = async (
  pagination: Pagination,
  contactId: string,
  name?: string | null
): Promise<DataWithPagination<ContactAddressRow>> => {
  let path = `/v1/contacts/${contactId}/addresses?name=`;

  if (name) path += `${name}`;
  return await paginatedApiCall(path, pagination, transformContactAddressRow);
};

/*---------------------------------------------------------------------------------------
        ADRESA - BY ID
-----------------------------------------------------------------------------------------*/
export const getContactAddressAPI = async (
  contactId: string,
  addressId: string
): Promise<ContactAddress> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/addresses/${addressId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactAddress(response.data);
};

/*---------------------------------------------------------------------------------------
        ADRESA - CREATE
-----------------------------------------------------------------------------------------*/
export const createContactAddressAPI = async (
  contactId: string,
  contactAddressEditFormValues: ContactAddressEditFormValues
): Promise<ContactAddress> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/addresses/`,
      method: "POST",
      body: transformContactAddressOnCreate(contactAddressEditFormValues),
    },
    {
      getMessage: {
        success: () => "Address je uspješno kreiran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactAddress(response.data);
};

/*---------------------------------------------------------------------------------------
        ADRESA - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateContactAddressAPI = async (
  contactId: string,
  addressId: string,
  contactAddressEditFormValues: ContactAddressEditFormValues
): Promise<ContactAddress> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/addresses/${addressId}`,
      method: "PUT",
      body: transformContactAddressOnUpdate(contactAddressEditFormValues),
    },
    {
      getMessage: {
        success: () => "Address je uspješno ažuriran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactAddress(response.data);
};


/*---------------------------------------------------------------------------------------
    CONTACT - ADDRESS -  DELETE
-----------------------------------------------------------------------------------------*/
export const deleteContactAddressAPI = async (
  t: (key: string) => string,
  contactId: string,
  addressId: string,
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/addresses/${addressId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.contact.address.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};


/*---------------------------------------------------------------------------------------
    NOTE - SEARCH
-----------------------------------------------------------------------------------------*/

export const getContactNotesAPI = async (
  pagination: Pagination,
  contactId: string,
  name?: string | null
): Promise<DataWithPagination<ContactNoteRow>> => {
  let path = `/v1/contacts/${contactId}/notes?name=`;

  if (name) path += `${name}`;
  return await paginatedApiCall(path, pagination, transformContactNoteRow);
};

/*---------------------------------------------------------------------------------------
          NOTE - BY ID
-----------------------------------------------------------------------------------------*/
export const getContactNoteAPI = async (
  contactId: string,
  noteId: string
): Promise<ContactNote> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/notes/${noteId}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactNote(response.data);
};

/*---------------------------------------------------------------------------------------
          NOTE - CREATE
-----------------------------------------------------------------------------------------*/
export const createContactNoteAPI = async (
  contactId: string,
  contactNoteEditFormValues: ContactNoteEditFormValues
): Promise<ContactNote> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/notes/`,
      method: "POST",
      body: transformContactNoteOnCreate(contactNoteEditFormValues),
    },
    {
      getMessage: {
        success: () => "Bilješka je uspješno kreiran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactNote(response.data);
};

/*---------------------------------------------------------------------------------------
          NOTE - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateContactNoteAPI = async (
  contactId: string,
  noteId: string,
  contactNoteEditFormValues: ContactNoteEditFormValues
): Promise<ContactNote> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/notes/${noteId}`,
      method: "PUT",
      body: transformContactNoteOnUpdate(contactNoteEditFormValues),
    },
    {
      getMessage: {
        success: () => "Bilješka je uspješno ažuriran",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformContactNote(response.data);
};

/*---------------------------------------------------------------------------------------
          NOTE - DELETE
-----------------------------------------------------------------------------------------*/
export const deleteContactNoteAPI = async (
  t: (key: string) => string,
  contactId: string,
  noteId: string,
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/notes/${noteId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.contact.note.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response.data?.deleted;
};


/*---------------------------------------------------------------------------------------
    CONTACT IMPORT
-----------------------------------------------------------------------------------------*/

export const contactImportAPI = async (file: File): Promise<ContactFile> => {
  const formData = new FormData();

  formData.append("file", file);

  const config = {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  };

  const response = await filesApi.post(
    `/v1/contacts/import/`,
    formData,
    config
  );

  return transformContactImport(response.data);
};

/*---------------------------------------------------------------------------------------
    CONTACT EXPORT
-----------------------------------------------------------------------------------------*/

export const contactExportAPI = async (): Promise<AxiosResponse> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/export`,
      method: "POST",
    },
    {
      getMessage: {
        success: () => "Datoteka je uspješno exportano",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return response;
};

/*---------------------------------------------------------------------------------------
  SUDREG LOOKUP
-----------------------------------------------------------------------------------------*/
export const sudRegLookupAPI = async (oib: string): Promise<SudRegResponse> => {
  const response = await createApiCall(
    {
      path: "/v1/contacts/sudreg",
      method: "POST",
      body: { oib },
    },
    undefined
  )();

  return response.data as SudRegResponse;
};

/*---------------------------------------------------------------------------------------
  CONTACT GROUP SEARCH
-----------------------------------------------------------------------------------------*/
export const getContactGroupsAPI = async (
  pagination: Pagination,
  name?: string | null,
  contactTypeId?: string | null
): Promise<DataWithPagination<ContactGroupRow>> => {
  let path = `/v1/contacts/groups?name=`;

  if (name) path += `${name}`;
  if (contactTypeId) path += `&contactTypeId=${contactTypeId}`;

  return await paginatedApiCall(path, pagination, transformContactGroupRow);
};


/*---------------------------------------------------------------------------------------
    CONTACT - RELATIONS
-----------------------------------------------------------------------------------------*/
export const getContactRelationsAPI = async (
  contactId: string
): Promise<ContactRelationRow[]> => {
  const response = await createApiCall({
    path: `/v1/contacts/${contactId}/relations`,
    method: "GET",
  })();
  return response.data ?? [];
};

export const createContactRelationAPI = async (
  contactId: string,
  formValues: ContactRelationEditFormValues
): Promise<ContactRelationRow> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/relations`,
      method: "POST",
      body: formValues,
    },
    {
      getMessage: {
        success: () => "Predstavnik je dodan",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();
  return response.data;
};

export const deleteContactRelationAPI = async (
  contactId: string,
  relationId: string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/contacts/${contactId}/relations/${relationId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Predstavnik je uklonjen",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();
  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
    CONTACT - IMPORT
-----------------------------------------------------------------------------------------*/
export const importContactXlsApi = async (
  formValues: ContactImportXlsFormValues
): Promise<ContactImportXlsResult[]> => {
  // const response = await createApiCall(
  //   {
  //     path: "/v1/contacts/import/xls",
  //     method: "POST",
  //     body: transformContactImportXlsOnCreate(formValues),
  //   },
  //   {
  //     getMessage: {
  //       success: () => "Datoteka je uspješno učitana",
  //       error: (error: any) => `${transformErrorResponse(error)}`,
  //     },
  //   }
  // )();

  const result: ContactImportXlsResult[] = [
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
    {
      id: "1",
      department: "HR",
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@example.com",
      phone: "123-456-7890",
    },
    {
      id: "2",
      department: "IT",
      firstName: "Jane",
      lastName: "Smith",
      email: "jane.smith@example.com",
      phone: "987-654-3210",
    },
    {
      id: "3",
      department: null,
      firstName: "Mike",
      lastName: "Johnson",
      email: null,
      phone: "555-666-7777",
    },
    {
      id: "4",
      department: "Finance",
      firstName: "Alice",
      lastName: "Brown",
      email: "alice.brown@example.com",
      phone: null,
    },
    {
      id: "5",
      department: "Marketing",
      firstName: null,
      lastName: "Davis",
      email: "davis@example.com",
      phone: "111-222-3333",
    },
  ];

  return result;
  // return response.data(transformContactImportXlsResult);
};

